import { defineSlotRecipe } from '@chakra-ui/react';
import { inputTheme } from './Input';

/*
 * v3 port notes:
 * - v2's `Select` (a styled native `<select>`) is `NativeSelect` in v3, whose
 *   slot recipe is keyed `nativeSelect` with slots `root`/`field`/`indicator`.
 *   v2's `field` part keeps its name; v2's `icon` part is v3's `indicator`.
 * - v2 reused `Input.variants` directly. `Input.ts` is a flat recipe in v3, so
 *   each of its variants is mapped onto the `field` slot (same technique as
 *   `NumberInput.ts`).
 * - v2's `defaultProps.colorScheme` has no recipe equivalent (and nothing in
 *   this theme read it), so it is dropped.
 * - v2's `icon` part also set `position: 'relative'` (copied from Chakra's
 *   own v2 default), but the icon wrapper's `position: absolute` baseStyle
 *   always beat it. v3 applies recipe styles last, so keeping it would pull
 *   the indicator out of the field and into the root's flex row — it is
 *   dropped, leaving the built-in absolute, vertically centered indicator.
 * - Registered in `theme.ts` as `nativeSelect`, merging over Chakra's
 *   built-in recipe (v2 registered it as `Select`).
 */
export const selectTheme = defineSlotRecipe({
  slots: ['root', 'field', 'indicator'],
  base: {
    field: {
      color: 'fg.primary',
      _disabled: {
        cursor: 'not-allowed',
        color: 'fg.muted',
        background: 'bg.muted',
        _placeholder: { color: 'transparent' },
      },
    },
    indicator: {
      width: '1.5rem',
      height: '100%',
      insetEnd: '0.5rem',
      color: 'currentColor',
      fontSize: '1.25rem',
      _disabled: {
        cursor: 'not-allowed',
        color: 'fg.muted',
        background: 'bg.muted',
        opacity: 'unset',
        _placeholder: { color: 'transparent' },
      },
      _focus: {
        color: 'tIndigo.base',
      },
    },
  },
  variants: {
    variant: Object.fromEntries(
      Object.entries(inputTheme.variants?.variant ?? {}).map(
        ([key, styles]) => [key, { field: styles }],
      ),
    ),
    size: {
      sm: { indicator: { fontSize: '0.45rem' } },
      md: { indicator: { fontSize: '0.625rem' } },
      lg: { indicator: { fontSize: '0.625rem' } },
      xs: { indicator: { insetEnd: '0.25rem' } },
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

export default selectTheme;
